import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FilePreview } from "@/components/FilePreview";
import { StatusBadge, ConfidenceBadge } from "@/components/StatusBadge";
import {
  Loader2, Download, Sparkles, ShieldCheck, FileClock, Save, Send, Archive, RefreshCw,
  AlertTriangle, Fingerprint, Calendar, Tag, FolderTree, Users2, Building2, Info, Mic, GitBranch, Package,
} from "lucide-react";

const ROLES = ["Creator", "Co-creator", "Designer", "Researcher", "Developer", "Strategist", "Editor", "Project lead", "Contributor", "Source provider", "Other"];

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } }

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-white/5 bg-[#111827] p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-white mb-3">{Icon && <Icon className="w-4 h-4 text-blue-400" />}{title}</div>
      {children}
    </div>
  );
}

export default function FileDetail() {
  const { id } = useParams();
  const { effectiveStaff } = useAuth();
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get(`/uploads/${id}`); setF(data); setEdit({ title: data.title, executive_summary: data.executive_summary, detailed_description: data.detailed_description, tags: (data.tags || []).join(", "), download_allowed: data.download_allowed, contribution: data.contribution || {}, sensitive_flags: data.sensitive_flags || [] }); } catch { setF(false); } };
  useEffect(() => { setF(null); load(); /* eslint-disable-next-line */ }, [id]);

  if (f === null) return <div className="flex justify-center h-96 items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (f === false) return <div className="text-center py-20 text-slate-500">File not available.</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/uploads/${id}`, {
        title: edit.title, executive_summary: edit.executive_summary, detailed_description: edit.detailed_description,
        tags: edit.tags.split(",").map((t) => t.trim()).filter(Boolean),
        download_allowed: edit.download_allowed, contribution: edit.contribution, sensitive_flags: edit.sensitive_flags,
      });
      toast.success("Changes saved"); await load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const action = async (a) => {
    setBusy(true);
    try { await api.post(`/uploads/${id}/${a}`); toast.success(a === "publish" ? "Published to CEO view" : `${a}d`); await load(); }
    catch { toast.error("Action failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-blue-400 mb-1 flex items-center gap-2">{f.kind} <ConfidenceBadge value={f.confidence} /></div>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-white tracking-tight break-words">{f.title}</h1>
          <div className="text-xs text-slate-500 mt-1 font-mono">{f.original_filename}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={f.status} />
          <a href={fileSrc(id, true)} data-testid="download-original" className="inline-flex items-center gap-1.5 text-sm border border-white/10 hover:bg-white/5 text-white rounded-lg px-3 py-2 transition-colors"><Download className="w-4 h-4" /> Original</a>
        </div>
      </div>

      {f.sensitive_flags?.length > 0 && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-2.5 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Flagged as potentially sensitive: {f.sensitive_flags.join(", ")}. Review before publishing.
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          <FilePreview file={f} />

          {/* Transcript (audio/video) */}
          {(f.transcript_segments?.length > 0 || f.transcript) && (
            <Section title="Transcript" icon={Mic}>
              <div className="text-[11px] text-amber-400/80 mb-3 flex items-center gap-1"><Info className="w-3 h-3" /> AI-generated transcript with timestamps &amp; speaker labels — verify against the recording.</div>
              {f.transcript_segments?.length > 0 ? (
                <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                  {f.transcript_segments.map((s, i) => (
                    <div key={i} data-testid={`transcript-seg-${i}`} className="flex gap-3 text-sm">
                      <span className="font-mono text-[11px] text-blue-400/80 flex-shrink-0 pt-0.5 w-24">{s.start}{s.end ? `–${s.end}` : ""}</span>
                      <div>
                        {s.speaker && <span className="text-blue-300 font-medium mr-2">{s.speaker}:</span>}
                        <span className="text-slate-300">{s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{f.transcript}</p>
              )}
            </Section>
          )}

          {/* Version family */}
          {f.versions?.length > 1 && (
            <Section title="Version Timeline" icon={GitBranch}>
              <div className="text-xs text-slate-500 mb-3">{f.versions.length} related versions grouped as one work. Earlier versions are always preserved.</div>
              <div className="relative pl-6 border-l border-white/10 space-y-4">
                {f.versions.map((v) => (
                  <div key={v.id} className="relative">
                    <div className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full ring-4 ring-[#111827] ${v.is_current ? "bg-emerald-500" : "bg-blue-500"}`} />
                    <Link to={`/file/${v.id}`} data-testid={`version-${v.id}`} className={`block group ${v.is_this ? "opacity-100" : "opacity-80 hover:opacity-100"}`}>
                      <div className="text-xs text-slate-500 font-mono">{fmt(v.date)}</div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${v.is_this ? "text-blue-300" : "text-white group-hover:text-blue-300"} transition-colors`}>{v.title}</span>
                        {v.is_current && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">latest</span>}
                        {v.is_this && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">viewing</span>}
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* AI Summary */}
          <Section title="AI Executive Summary" icon={Sparkles}>
            <div className="text-[11px] text-amber-400/80 mb-2 flex items-center gap-1"><Info className="w-3 h-3" /> AI-generated — verify against the original source.</div>
            {effectiveStaff ? (
              <textarea value={edit.executive_summary} onChange={(e) => setEdit({ ...edit, executive_summary: e.target.value })} rows={3} className="w-full bg-[#070B14] border border-white/10 rounded-lg p-3 text-sm text-slate-200 outline-none focus:border-blue-500" data-testid="edit-summary" />
            ) : <p className="text-slate-200 leading-relaxed">{f.executive_summary}</p>}
          </Section>

          <Section title="Detailed Description" icon={FileClock}>
            {effectiveStaff ? (
              <textarea value={edit.detailed_description} onChange={(e) => setEdit({ ...edit, detailed_description: e.target.value })} rows={5} className="w-full bg-[#070B14] border border-white/10 rounded-lg p-3 text-sm text-slate-200 outline-none focus:border-blue-500" data-testid="edit-description" />
            ) : <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{f.detailed_description}</p>}
          </Section>

          {/* Dates */}
          {f.dates?.length > 0 && (
            <Section title="Dates & Chronology" icon={Calendar}>
              <div className="space-y-2">
                {f.dates.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{d.value} — <span className="text-slate-500">{d.label}</span></span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${d.certainty === "high" ? "text-emerald-400 border-emerald-500/20 bg-emerald-500/10" : d.certainty === "low" ? "text-red-400 border-red-500/20 bg-red-500/10" : "text-amber-400 border-amber-500/20 bg-amber-500/10"}`}>{d.certainty} certainty</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {effectiveStaff && (
            <Section title="Classification & Publishing" icon={ShieldCheck}>
              <label className="text-xs text-slate-500">Title</label>
              <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mb-3 mt-1 outline-none focus:border-blue-500" data-testid="edit-title" />
              <label className="text-xs text-slate-500">Tags (comma separated)</label>
              <input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mb-3 mt-1 outline-none focus:border-blue-500" data-testid="edit-tags" />
              <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                <input type="checkbox" checked={!!edit.download_allowed} onChange={(e) => setEdit({ ...edit, download_allowed: e.target.checked })} data-testid="edit-download-allowed" /> Allow CEO to download original
              </label>
              <button onClick={save} disabled={saving} data-testid="save-file" className="w-full inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm rounded-lg px-3 py-2 transition-colors mb-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {f.status !== "published"
                  ? <button onClick={() => action("publish")} disabled={busy} data-testid="publish-file-btn" className="inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-3 py-2 transition-colors"><Send className="w-3.5 h-3.5" /> Publish</button>
                  : <button onClick={() => action("unpublish")} disabled={busy} data-testid="unpublish-file-btn" className="inline-flex items-center justify-center gap-1.5 border border-white/10 hover:bg-white/5 text-slate-300 text-sm rounded-lg px-3 py-2 transition-colors">Unpublish</button>}
                <button onClick={() => action("archive")} disabled={busy} data-testid="archive-file-btn" className="inline-flex items-center justify-center gap-1.5 border border-white/10 hover:bg-white/5 text-slate-300 text-sm rounded-lg px-3 py-2 transition-colors"><Archive className="w-3.5 h-3.5" /> Archive</button>
              </div>
              <button onClick={() => action("reprocess")} disabled={busy} data-testid="reprocess-file-btn" className="w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"><RefreshCw className="w-3.5 h-3.5" /> Re-run AI analysis</button>
            </Section>
          )}

          {/* Categories */}
          {(f.categories?.length > 0) && (
            <Section title="Categories" icon={FolderTree}>
              <div className="space-y-1.5">
                {f.categories.map((c) => (
                  <Link key={c.id} to={`/category/${c.id}`} className="block text-sm text-slate-300 hover:text-blue-300 transition-colors">
                    {(c.path || [c.name]).join(" › ")} <span className="text-[10px] text-slate-600 uppercase">{c.type}</span>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {(f.people?.length > 0 || f.organizations_r?.length > 0) && (
            <Section title="People & Organizations" icon={Users2}>
              <div className="flex flex-wrap gap-2">
                {f.people?.map((p) => <Link key={p.id} to={`/entity/${p.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 hover:text-white"><Users2 className="w-3 h-3" />{p.name}</Link>)}
                {f.organizations_r?.map((o) => <Link key={o.id} to={`/entity/${o.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 hover:text-white"><Building2 className="w-3 h-3" />{o.name}</Link>)}
              </div>
            </Section>
          )}

          {f.tags?.length > 0 && (
            <Section title="Tags" icon={Tag}>
              <div className="flex flex-wrap gap-2">{f.tags.map((t) => <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="text-xs px-2.5 py-1 rounded-full bg-blue-600/10 text-blue-300 border border-blue-500/20 hover:bg-blue-600/20">{t}</Link>)}</div>
            </Section>
          )}

          {/* Contribution — private/staff only */}
          {effectiveStaff && (
            <Section title="My Contribution (private)" icon={Info}>
              <label className="text-xs text-slate-500">Role</label>
              <select value={edit.contribution?.role || ""} onChange={(e) => setEdit({ ...edit, contribution: { ...edit.contribution, role: e.target.value } })} data-testid="contribution-role" className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mb-3 mt-1 outline-none focus:border-blue-500">
                <option value="">— select —</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <label className="text-xs text-slate-500">Note</label>
              <textarea value={edit.contribution?.note || ""} onChange={(e) => setEdit({ ...edit, contribution: { ...edit.contribution, note: e.target.value } })} rows={2} className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-slate-200 mb-3 mt-1 outline-none focus:border-blue-500" data-testid="contribution-note" />
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={!!edit.contribution?.publish_approved} onChange={(e) => setEdit({ ...edit, contribution: { ...edit.contribution, publish_approved: e.target.checked } })} data-testid="contribution-publish" /> Approve contribution for CEO view
              </label>
            </Section>
          )}

          {/* Provenance — staff only */}
          {effectiveStaff && f.provenance && (
            <Section title="Provenance Record" icon={Fingerprint}>
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-2"><dt className="text-slate-500">SHA-256</dt><dd className="text-slate-400 font-mono truncate max-w-[180px]" title={f.sha256}>{f.sha256?.slice(0, 20)}…</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Uploaded by</dt><dd className="text-slate-300">{f.provenance.uploaded_by}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Upload time</dt><dd className="text-slate-300">{fmt(f.provenance.upload_time)}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">File created</dt><dd className="text-slate-300">{fmt(f.file_created_at)}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Size</dt><dd className="text-slate-300">{(f.size / 1024).toFixed(1)} KB</dd></div>
                {f.from_archive && <div className="flex items-center gap-1.5 text-slate-400 pt-1"><Package className="w-3.5 h-3.5 text-blue-400" /> Extracted from archive: <span className="text-slate-300 truncate">{f.from_archive}</span></div>}
                {f.is_duplicate && <div className="text-amber-400 pt-1">Detected as a duplicate — original preserved.</div>}
              </dl>
            </Section>
          )}
        </aside>
      </div>
    </div>
  );
}
